const DB_NAME = 'auction_cache';
const DB_VERSION = 2;

interface PaymentCache {
    paymentMethods: any[];
    lastFetched: number;
    auctionId: number;
    userId: string;
    selectedPaymentId: string | null;
    persisted: boolean;
}

export class StorageService {
    private static PAYMENT_STORE = 'payment_cache';
    private static CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

    private static async getDb(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Check if store exists before creating it
                if (!db.objectStoreNames.contains(this.PAYMENT_STORE)) {
                    // Create store with compound key of auctionId and userId
                    const store = db.createObjectStore(this.PAYMENT_STORE, { keyPath: ['auctionId', 'userId'] });
                    // Add indexes for individual lookups
                    store.createIndex('auctionId', 'auctionId', { unique: false });
                    store.createIndex('userId', 'userId', { unique: false });
                }
            };
        });
    }

    static async getPaymentCache(auctionId: number, userId: string | undefined): Promise<PaymentCache | null> {
        if (!auctionId || typeof userId === 'undefined') {
            console.log('Missing auctionId or userId', auctionId, userId);
            return null;
        }

        try {
            const db = await this.getDb();
            const transaction = db.transaction([this.PAYMENT_STORE], 'readonly');
            const store = transaction.objectStore(this.PAYMENT_STORE);

            return new Promise<PaymentCache | null>((resolve) => {
                const request = store.get([auctionId, userId]);

                request.onerror = () => {
                    resolve(null);
                };

                request.onsuccess = () => {
                    const result = request.result;

                    if (!result) {
                        resolve(null);
                    } else if (Date.now() - result.lastFetched > this.CACHE_DURATION) {
                        resolve(null);
                    } else {
                        resolve(result);
                    }
                };
            });
        } catch (error) {
            console.error('Error getting payment cache:', error);
            return null;
        }
    }

    static async setPaymentCache(
        auctionId: number,
        paymentMethods: any[],
        selectedPaymentId: string | null,
        userId: string | undefined,
        persisted: boolean = false
    ): Promise<void> {
        if (!auctionId || typeof userId === 'undefined') {
            console.log('Not caching - missing auctionId or userId', auctionId, userId);
            return;
        }

        try {
            const db = await this.getDb();
            const transaction = db.transaction([this.PAYMENT_STORE], 'readwrite');
            const store = transaction.objectStore(this.PAYMENT_STORE);

            const request = store.put({
                auctionId,
                userId,
                paymentMethods,
                selectedPaymentId,
                persisted,
                lastFetched: Date.now(),
            });

            request.onerror = () => { throw request.error; };
            request.onsuccess = () => { };
        } catch (error) {
            console.error('Error setting payment cache:', error);
        }
    }

    static async clearPaymentCache(): Promise<void> {
        const db = await this.getDb();
        const transaction = db.transaction([this.PAYMENT_STORE], 'readwrite');
        const store = transaction.objectStore(this.PAYMENT_STORE);
        store.clear();
    }
} 